import React, { useRef, useState, useContext } from "react";
import { Modal, Row, Col, Input } from "reactstrap";
import SignaturePad from "react-signature-canvas";
import { toast } from "react-toastify";
import { FriendRequest } from "../../models/FriendRequest";
import UserContext from "../../context/UserContext";
import { AuthService, ConsentData } from "../../services/AuthService";

interface ConsentModalProps {
  isOpen: boolean;
  toggle: () => void;
  matchedTherapists: FriendRequest[];
}

const ConsentModal: React.FC<ConsentModalProps> = ({ isOpen, toggle, matchedTherapists }) => {
  const [user] = useContext(UserContext);
  const [consentSignature, setConsentSignature] = useState<string>("");
  const [isSignatureSaved, setIsSignatureSaved] = useState(false);
  const [legalGuardianSignature, setLegalGuardianSignature] = useState<string>("");
  const [isLegalGuardianSignatureSaved, setIsLegalGuardianSignatureSaved] = useState(false);
  const [legalGuardianName, setLegalGuardianName] = useState<string>("");
  const [showLegalGuardian, setShowLegalGuardian] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const signPad = useRef<SignaturePad>(null);
  const legalGuardianSignPad = useRef<SignaturePad>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const signatureRef = useRef<HTMLDivElement>(null);

  // Scroll navigation function
  const scrollToSignature = () => {
    if (signatureRef.current && modalBodyRef.current) {
      signatureRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const getAssignedTherapistReferralLink = () => {
    // Check if user has any matched therapists
    if (matchedTherapists && matchedTherapists.length > 0) {
      const firstMatchedTherapist = matchedTherapists[0].therapistId;
      if (firstMatchedTherapist && firstMatchedTherapist.firstname && firstMatchedTherapist.lastname) {
        return `https://mylavni.com/therapist-profile/${firstMatchedTherapist.firstname}-${firstMatchedTherapist.lastname}`;
      }
    }
    // If no matched therapist, return dashboard
    return "https://mylavni.com/dashboard";
  };

  const clearSignature = () => {
    if (signPad.current) {
      signPad.current.clear();
      setConsentSignature("");
      setIsSignatureSaved(false);
    }
  };

  const saveSignature = () => {
    if (signPad.current) {
      const signatureData = signPad.current.getTrimmedCanvas().toDataURL("image/png");
      setConsentSignature(signatureData);
      setIsSignatureSaved(true);
    }
  };

  const clearLegalGuardianSignature = () => {
    if (legalGuardianSignPad.current) {
      legalGuardianSignPad.current.clear();
      setLegalGuardianSignature("");
      setIsLegalGuardianSignatureSaved(false);
    }
  };

  const saveLegalGuardianSignature = () => {
    if (legalGuardianSignPad.current) {
      const signatureData = legalGuardianSignPad.current.getTrimmedCanvas().toDataURL("image/png");
      setLegalGuardianSignature(signatureData);
      setIsLegalGuardianSignatureSaved(true);
    }
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const submitConsentForm = async () => {
    // If Legal Guardian is required, only validate Legal Guardian fields
    if (showLegalGuardian) {
      if (!legalGuardianSignature) {
        toast.error("Please provide legal guardian signature before submitting.", {
          position: toast.POSITION.BOTTOM_RIGHT,
          className: "foo-bar",
        });
        return;
      }

      if (!legalGuardianName.trim()) {
        toast.error("Please provide legal guardian name before submitting.", {
          position: toast.POSITION.BOTTOM_RIGHT,
          className: "foo-bar",
        });
        return;
      }
    } else {
      // If Legal Guardian is NOT required, validate Client signature
      if (!consentSignature) {
        toast.error("Please provide your signature before submitting.", {
          position: toast.POSITION.BOTTOM_RIGHT,
          className: "foo-bar",
        });
        return;
      }
    }

    try {
      setIsSubmitting(true);

      // Prepare consent data
      const consentData: ConsentData = {
        clientName: user?.firstname + " " + user?.lastname,
        clientSignatureBase64: consentSignature, // Send client signature if available
        clientSignDate: new Date().toISOString(),
        is_legal_guardian_required: showLegalGuardian,
        ...(showLegalGuardian && {
          legalGuardianName: legalGuardianName.trim(),
          legalGuardianSignatureBase64: legalGuardianSignature,
          legalGuardianSignDate: new Date().toISOString()
        })
      };

      console.log("Submitting consent data:", consentData);

      // Call API through service
      const result = await AuthService.createClientConsentData(consentData);
      console.log("Consent submission result:", result);

      if (!result.success) {
        throw new Error(result.message || "Failed to submit consent form");
      }

      toast.success("Consent form submitted successfully!", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });

      // Reset form and close modal
      toggle();
      setConsentSignature("");
      setIsSignatureSaved(false);
      setLegalGuardianSignature("");
      setIsLegalGuardianSignatureSaved(false);
      setLegalGuardianName("");
    } catch (error) {
      console.error("Error submitting consent form:", error);
      toast.error("Failed to submit consent form. Please try again.", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="modal-lg">
      <div className="modal-header">
        <h5 className="modal-title">Consent Form Required</h5>
        <button
          type="button"
          onClick={toggle}
          className="close"
          data-dismiss="modal"
          aria-label="Close"
        >
          <span>&times;</span>
        </button>
      </div>
      <div style={{ position: "relative" }}>
        {/* Sticky Navigation Panel */}
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 1000
          }}
        >
          <button
            type="button"
            className="btn btn-outline-success btn-sm"
            onClick={scrollToSignature}
            style={{
              borderRadius: "20px",
              fontSize: "12px",
              padding: "6px 12px",
              fontWeight: "500",
              whiteSpace: "nowrap",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              backgroundColor: "white"
            }}
          >
            ✍️ Sign to Complete
          </button>
        </div>

        <div className="modal-body" ref={modalBodyRef} style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "140px" }}>
          {/* <div className="text-center mb-4">
            <i className="bx bx-file-blank" style={{ fontSize: "48px", color: "#F47B20" }}></i>
          </div> */}
        <div className="mb-4" style={{ lineHeight: "1.6", fontSize: "14px", color: "#495057" }}>
          <div className="text-center mb-4">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
              {/* <div style={{ color: "#F47B20", fontSize: "24px" }}>☀</div>
              <h4 style={{ color: "#F47B20", fontWeight: "bold", margin: 0 }}>Lavni</h4> */}
            </div>
            <h3 style={{ fontWeight: "bold", marginTop: "20px", marginBottom: "20px" }}>CONSENT AGREEMENT</h3>
          </div>

          <p className="mb-3">
            This Agreement contains information about the professional services and business policies of Lavni Inc. It also contains
            information about the Health Insurance Portability and Accountability Act (HIPAA), a federal law that provides privacy
            protection and patient rights regarding use and disclosure of your Protected Health Information (PHI) for purposes of
            treatment, payment, and health care operations, and how we will use your PHI. Please read this <em>Consent Agreement</em>
            carefully. Your signature at the end signifies your consent to the Agreement and to treatment. You may revoke your
            consent in writing at any time via email to <a href="mailto:compliance@mylavni.com">compliance@mylavni.com</a> or fax to 1-888-245-5107.
          </p>

          <p className="mb-3">
            <strong>About Your Clinician:</strong> Information about your clinician can be found on their clinician page on the Lavni website at{" "}
            <span
              style={{
                backgroundColor: "#FFFF00",
                color: "#000",
                fontWeight: "600",
                cursor: "pointer",
                padding: "2px 4px"
              }}
              onClick={() => {
                const referralLink = getAssignedTherapistReferralLink();
                window.open(referralLink, "_blank");
              }}
            >
              {getAssignedTherapistReferralLink()}
            </span>
            . You will be provided with additional information directly from your assigned therapist regarding the specific interventions you will use during your time together. Lavni
            therapists practice under the guidance of a platform Clinical Director, and all therapists practice according to the rules
            and regulations of discipline-specific licensing boards. If your therapist is an "Associate" clinician, his/her practice is
            conducted under the supervision of a fully licensed mental health professional. You are free to ask questions at any
            time about your therapist's background, experience, and clinical orientation. At the end of each session, you will be
            provided with an opportunity to contact the Lavni Clinical Director with concerns should any exist. Additional contact
            information for any supervising entity will be provided to you should concerns presented remain unresolved.
          </p>

          <p className="mb-3">
            <strong>Treatment:</strong> Treatment begins with an evaluation by your clinician. The clinician will formulate a diagnosis as the basis for
            treatment. The completed evaluation is called the Comprehensive Clinical Assessment (CCA). Feel free to ask questions
            about this diagnosis. The clinician will work with you next to develop an individualized therapy plan. While the CCA is a
            clinical document that is not released directly to the client, you will receive a copy of your therapy plan. Should a copy of
            your CCA be required for coordination of care with another professional, it will be released directly to that professional
            with your written consent.
          </p>

          <p className="mb-3">
            Lavni provides telehealth-based psychotherapy for individuals, couples, families, and groups. <em>Telehealth</em> is a mode of
            delivering health care services, including psychotherapy, via communication technologies (e.g., Internet or phone) to
            facilitate diagnosis, consultation, treatment, education, care management, and self-management of a patient's health
            care. Psychotherapy requires active effort on the client's part. To be successful, psychotherapy requires that clients
            work during and outside of sessions.
          </p>

          <p className="mb-3">
            In addition to using telehealth for service delivery, Lavni uses HIPAA-compliant AI tools to assist clinicians in creating
            accurate and timely documentation of your sessions. Your clinician reviews and finalizes all clinical documentation.
            Your information remains secure, and AI does not make decisions about your care. You may decline the use of AI at any
            time without affecting your treatment. Contact our compliance team at <a href="mailto:compliance@mylavni.com">compliance@mylavni.com</a> if you have
            questions or wish to withdraw consent for AI-assisted documentation.
          </p>

          <p className="mb-3">
            Lavni provides evidence-based psychotherapy for individuals who can successfully engage in treatment without need
            for crisis response. Our clinicians utilize interventions such as cognitive behavioral therapy (CBT), motivational
            interviewing, dialectical behavior therapy (DBT), and eye movement desensitization and reprocessing (EMDR) to assist
            clients with reaching their goals. While we can address a wide variety of symptoms, we are not the best fit for
            individuals who are at high risk for self-harm or harm to others as they may benefit more from a higher level of care.
            Clinicians will advise you of your appropriateness for treatment with Lavni during your CCA and alert you if there are
            any changes during your time in treatment.
          </p>


          <p className="mb-3">
            Psychotherapy can have benefits and risks. Since it often involves discussing unpleasant aspects of life, people may
            experience uncomfortable feelings like sadness, guilt, anger, frustration, loneliness, and helplessness. It is important
            that parents/legal guardians understand sessions with minors may include discussions of mature topics including
            those regarding sexual activity. Parents/legal guardians must alert therapists in writing if they disapprove of these
            interventions. Your therapist will discuss the potential impact on treatment of such a decision. Psychotherapy has been
            shown to have benefits such as improved relationships, more productive problem solving, reduction of distress, and
            increased sense of well-being. There are, however, no guarantees about what the experience will be.
          </p>

          <p className="mb-3">
            Telehealth can have benefits and risks. In some instances, telehealth may not be as effective or provide the same
            results as in-person therapy. If my therapist believes I would be better served by in-person therapy, they will discuss
            this with me and refer me to in-person services as needed. There is a possibility that treatment sessions and
            transmission of treatment information could be disrupted or distorted by technical failures. Despite reasonable efforts
            and safeguards on the part of therapists, sessions could be interrupted or accessed by unauthorized persons, and the
            electronic storage of treatment information could be accessed by unauthorized persons. Miscommunication between
            the client and the therapist could also occur. Telehealth removes barriers to treatment such as access, distance, and
            scheduling difficulties.
          </p>

          <p className="mb-3">
            <strong>Telehealth Session Recording and Transcription:</strong>
          </p>

          <ol className="mb-3" style={{ paddingLeft: "20px" }}>
            <li className="mb-2">
              <strong>Consent Requirement:</strong> Recording or transcription of sessions will only occur if both the client and therapist
              provide explicit consent before each session.
            </li>
            <li className="mb-2">
              <strong>Purpose of Recording:</strong> Recordings and transcriptions are intended to improve the quality of care and assist with
              clinical documentation and quality assurance.
            </li>
            <li className="mb-2">
              <strong>Right to Decline:</strong> Clients have the right to decline recording and transcription of any session without affecting
              their access to telehealth services.
            </li>
            <li className="mb-2">
              <strong>Confidentiality and Storage:</strong> Any recordings or transcriptions will be stored securely in compliance with HIPAA
              regulations and will only be used for the purposes stated above unless additional consent is obtained.
            </li>
          </ol>

          <p className="mb-3">
            <strong>Appointments:</strong> Clinicians are available <em>by appointment only</em> for sessions that last approximately one (1) hour long.
            Lavni clinicians provide telehealth sessions ONLY. Appointments can be scheduled through coordination with clinicians,
            use of the web portal, or by calling 980-890-7995 for assistance. Please log in for at least 5 minutes prior to
            appointments to confirm that there are no technological issues that might prevent a timely start to session. Late
            arrivals to session of more than fifteen (15) minutes are considered a "no show." Three (3) session "no shows" may
            result in a discharge from services. Please communicate any need to adjust appointment day/date/time directly with
            your assigned clinician to avoid "no shows."
          </p>

          <p className="mb-3">
            <strong>EMERGENCIES</strong><br/>
            If you are a Lavni client experiencing a mental health emergency related to feelings of dangerousness to self during a
            time when your Lavni clinician is unavailable, you can contact the National Suicide Prevention Lifeline at 1-800-273-
            8255. If you are a Lavni client experiencing a mental health emergency related to feelings of dangerousness to self or
            others or impaired judgment during a time when the Lavni Client Support Team (980-890-7995) is unavailable, you may
            contact the Lavni Crisis Line at 984-254-8961 for directions on how to access crisis care. If you feel immediately unsafe,
            please call 911 or seek help from a hospital or crisis-oriented health care facility closest to you.
          </p>

          <p className="mb-3">
            Please be advised that all calls made to the Lavni Crisis Line deemed to be of a non-emergent nature will be redirected
            for outreach by our Client Support Team during regular business hours. During sessions, if you are assessed to be at
            high risk for harm to self or others, your clinician will advise you to seek help. If you do not seek help voluntarily, it is
            the Lavni policy that the clinician sends emergency personnel to ensure your safety. As the Lavni platform is not
            designed for crisis response and management, individuals with crisis needs will be evaluated to determine the most
            appropriate care options moving forward.
          </p>

          <p className="mb-3">
            <strong>PRIVACY AND CONFIDENTIALITY</strong>
          </p>

          <p className="mb-3">
            We follow HIPAA and state regulations in assuring the privacy of your Protected Health Information (PHI). Please read
            the <a href="https://mylavni.com/privacy-2/"><strong>PRIVACY NOTICE</strong></a> form provided. Your signature on this <em>Consent Agreement</em> indicates that you have read and
            understood the content of the Privacy Notice. Your therapist will use Lavni's HIPAA-compliant platform for telehealth
            sessions and all reasonable and appropriate efforts will be made to protect the confidentiality and security of your
            PHI. In the event of a breach of unsecured PHI, Lavni will notify you in accordance with HIPAA regulations.
          </p>

          <p className="mb-3">
            <strong>MINORS or ADULTS ADJUDICATED INCOMPETENT</strong>
          </p>

          <p className="mb-3">
            Lavni policy regarding rules for consent for treatment, request to review, inspect or amend the client PHI, or consent to
            or authorize release of information from the client's PHI are determined based upon HIPAA and state guidelines for the
            state of residence of the person receiving services. For minors, a <em>PARENT/LEGAL GUARDIAN CONSENT FOR
            COUNSELING WITH MINORS</em> form outlining conditions with be provided and signed. For adults adjudicated
            incompetent, a <em>LEGAL GUARDIAN CONSENT FOR INDIVIDUAL COUNSELING</em> form outlining conditions will be provided
            and signed.
          </p>

          <p className="mb-3">
            <strong>PAYMENT</strong>
          </p>

          <p className="mb-3">
            Services are available through direct payment and through coverage by insurance benefits. A comprehensive list of
            accepted insurance is maintained and updated regularly on the Lavni website. To support individuals seeking treatment
            who are experiencing financial hardships, review for fee adjustments is available with submission of a request to{" "}
            <a href="mailto:billing@mylavni.com">billing@mylavni.com</a>, Subject: FEE ADJUSTMENT. If we are providers with your insurance company, you must make
            your co-pay at each visit and your deductible annually. You will be prompted to pay your copayment when you log on
            for you session with your clinician. If we are not providers for your insurance company or you do not have insurance
            coverage, you will be responsible for the entire fee at the time of the appointment. It is your responsibility to alert us
            of any changes in your insurance plan. If we have made all efforts to comply with your insurance carrier, but the
            company refuses payment or you have exhausted the limits of your policy, you will be responsible for payment.
          </p>

          <p className="mb-3">
            <strong>MANAGED CARE</strong>
          </p>

          <p className="mb-3">
            If your managed care plan requires utilization review (UR) to authorize continued reimbursement for services, you are
            consenting to PHI being reported to in the form of written or verbal reports. Even if you and your clinician have
            complied with all the UR requirements, there is no guarantee that your insurance company will authorize further
            treatment. In the case of a disagreement between your managed care plan and clinician's recommendation, your
            clinician will notify you about the suggested next steps in your treatment process.
          </p>

          <p className="mb-3">
            <strong>SIGNATURE</strong>
          </p>

          <p className="mb-3">
            Your signature certifies that you: (1) have read and understand, and agree to the information outlined in this <em>Consent
            Agreement</em>; (2) received a copy of, read, and understand the <em>Privacy Notice</em>; (3) consent to the use of telehealth in care
            (4) understand that you have the right to have all questions regarding this information answered to your satisfaction,
            and can email <a href="mailto:compliance@mylavni.com">compliance@mylavni.com</a> with any questions or to coordinate a virtual meeting for review.
          </p>
        </div>

        {/* Signature Section */}
        <div className="mb-4" ref={signatureRef}>
          <div className="mb-3 d-flex align-items-center">
            <h6 className="mb-0 me-3" style={{ fontWeight: "600" }}>Signature Section:</h6>
            <button
              type="button"
              className={`btn btn-sm ${showLegalGuardian ? 'btn-success' : 'btn-outline-secondary'}`}
              onClick={() => setShowLegalGuardian(!showLegalGuardian)}
              style={{
                borderRadius: "6px",
                fontSize: "13px",
                padding: "4px 12px",
                fontWeight: "500"
              }}
            >
              {showLegalGuardian ? '✓ Legal Guardian Required' : 'Legal Guardian Required'}
            </button>
          </div>

          {/* Client Signature Row */}
          <Row className="mb-4">
            <Col md={4}>
              <div className="mb-2">
                <label className="form-label" style={{ fontWeight: "500", fontSize: "14px" }}>
                  Client Name
                </label>
                <div style={{ borderBottom: "1px solid #000", paddingBottom: "8px", minHeight: "30px" }}>
                  {user?.firstname} {user?.lastname}
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-2">
                <label className="form-label" style={{ fontWeight: "500", fontSize: "14px" }}>
                  Client Signature
                </label>
                <div style={{ border: "1px solid #ddd", borderRadius: "4px", minHeight: "80px", backgroundColor: "#f8f9fa" }}>
                  {isSignatureSaved ? (
                    <img
                      src={consentSignature}
                      alt="Client Signature"
                      style={{ width: "100%", height: "80px", objectFit: "contain" }}
                    />
                  ) : (
                    <SignaturePad
                      backgroundColor="rgb(248,249,250)"
                      ref={signPad}
                      penColor="black"
                      canvasProps={{
                        style: { width: "100%", height: "80px" }
                      }}
                    />
                  )}
                </div>
                {!isSignatureSaved ? (
                  <div className="d-flex gap-1 mt-1">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={clearSignature}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={saveSignature}
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mt-1"
                    onClick={() => setIsSignatureSaved(false)}
                  >
                    Edit
                  </button>
                )}
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-2">
                <label className="form-label" style={{ fontWeight: "500", fontSize: "14px" }}>
                  Date
                </label>
                <div style={{ borderBottom: "1px solid #000", paddingBottom: "8px", minHeight: "30px" }}>
                  {getCurrentDate()}
                </div>
              </div>
            </Col>
          </Row>

          {/* Legal Guardian Row */}
          {showLegalGuardian && (
            <Row className="mb-3">
              <Col md={4}>
                <div className="mb-2">
                  <label className="form-label" style={{ fontWeight: "500", fontSize: "14px" }}>
                    Legal Guardian Name*
                  </label>
                  <Input
                    type="text"
                    value={legalGuardianName}
                    onChange={(e) => setLegalGuardianName(e.target.value)}
                    placeholder="Enter legal guardian name"
                    style={{ borderBottom: "1px solid #000", borderTop: "none", borderLeft: "none", borderRight: "none", borderRadius: "0" }}
                  />
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-2">
                  <label className="form-label" style={{ fontWeight: "500", fontSize: "14px" }}>
                    Legal Guardian Signature
                  </label>
                  <div style={{ border: "1px solid #ddd", borderRadius: "4px", minHeight: "80px", backgroundColor: "#f8f9fa" }}>
                    {isLegalGuardianSignatureSaved ? (
                      <img
                        src={legalGuardianSignature}
                        alt="Legal Guardian Signature"
                        style={{ width: "100%", height: "80px", objectFit: "contain" }}
                      />
                    ) : (
                      <SignaturePad
                        backgroundColor="rgb(248,249,250)"
                        ref={legalGuardianSignPad}
                        penColor="black"
                        canvasProps={{
                          style: { width: "100%", height: "80px" }
                        }}
                      />
                    )}
                  </div>
                  {!isLegalGuardianSignatureSaved ? (
                    <div className="d-flex gap-1 mt-1">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={clearLegalGuardianSignature}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={saveLegalGuardianSignature}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm mt-1"
                      onClick={() => setIsLegalGuardianSignatureSaved(false)}
                    >
                      Edit
                    </button>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-2">
                  <label className="form-label" style={{ fontWeight: "500", fontSize: "14px" }}>
                    Date
                  </label>
                  <div style={{ borderBottom: "1px solid #000", paddingBottom: "8px", minHeight: "30px" }}>
                    {getCurrentDate()}
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {showLegalGuardian && (
            <div className="mb-3">
              <small className="text-muted">*If Applicable</small>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="d-flex justify-content-center">
          <button
            type="button"
            className="btn btn-success"
            onClick={submitConsentForm}
            disabled={
              isSubmitting ||
              (showLegalGuardian
                ? (!legalGuardianSignature || !legalGuardianName.trim())
                : !consentSignature
              )
            }
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Submitting...
              </>
            ) : (
              "Submit Consent Form"
            )}
          </button>
        </div>
      </div>
      </div>
    </Modal>
  );
};

export default ConsentModal;
